import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { clearLocalData, getMeta, reference, storageEstimate } from '../lib/localdb.js';
import { apiJson, loadSession, type Session } from '../lib/api.js';
import { isReporting } from '../lib/geo.js';
import { platformAuthenticatorAvailable } from '../lib/lock.js';
import { lastSyncedAt } from '../lib/sync.js';
import { shortTime } from '../lib/format.js';
import { BigButton } from '../components/Controls.js';

/**
 * The phone itself.
 *
 * Everything on this screen is a promise the app has made and has to be able to show it is
 * keeping: that position reporting is visible when it is on, that storage will not be evicted,
 * that the device can be named and taken away, and that signing out does not lose a day of
 * unsent hours.
 */

type Device = {
  id: string;
  label: string | null;
  userAgent: string | null;
  lastUsedAt: string | null;
  current: boolean;
  revokedAt: string | null;
};

export function SettingsScreen() {
  const [session, setSession] = useState<Session | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [storage, setStorage] = useState<{ usedMb: number; quotaMb: number } | null>(null);
  const [persisted, setPersisted] = useState<string | null>(null);
  const [synced, setSynced] = useState<Date | null>(null);
  const [biometrics, setBiometrics] = useState(false);
  const [positionEnabled, setPositionEnabled] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setSession(await loadSession());
      setStorage(await storageEstimate());
      setPersisted(await getMeta('storagePersisted'));
      setSynced(await lastSyncedAt());
      setBiometrics(await platformAuthenticatorAvailable());
      const settings = await reference<{ positionEnabled?: boolean }>('settings', {});
      setPositionEnabled(Boolean(settings.positionEnabled));
      try {
        const list = await apiJson<{ devices: Device[] }>('/auth/devices');
        setDevices(list.devices);
      } catch {
        setDevices([]);
      }
    })();
  }, []);

  return (
    <div className="settings">
      <header className="stopscreen__head">
        <Link to="/" className="back" aria-label="Retour">
          ‹
        </Link>
        <div className="stopscreen__title">
          <h1>Cet appareil</h1>
          <p>{session?.name ?? '—'}</p>
        </div>
      </header>

      <section className="settings__block">
        <h2>Synchronisation</h2>
        <p>Dernière synchronisation : {synced ? shortTime(synced) : 'jamais'}</p>
        <p className="quiet">
          Stockage {storage ? `${storage.usedMb} Mo sur ${storage.quotaMb} Mo` : 'inconnu'} ·{' '}
          {persisted === 'true' ? 'protégé contre l’effacement' : 'non protégé'}
        </p>
        {persisted !== 'true' && (
          <p className="warn">
            Installez l’application sur l’écran d’accueil : sans cela, le navigateur peut effacer le travail non
            envoyé.
          </p>
        )}
      </section>

      {/*
        The indicator the position design promises. A technician can always see that it is on,
        and this says exactly what is collected rather than only that something is.
      */}
      <section className="settings__block">
        <h2>Position</h2>
        {positionEnabled ? (
          <>
            <p className={isReporting() ? 'positive' : ''}>
              {isReporting() ? 'Transmise pendant le quart de travail' : 'Activée, en attente du premier départ'}
            </p>
            <p className="quiet">
              Une position approximative toutes les quelques minutes entre le premier « en route » et le dernier
              arrêt terminé. Jamais la nuit, jamais un jour de congé. La répartition la voit ; personne d’autre.
            </p>
          </>
        ) : (
          <p className="quiet">Désactivée par l’entreprise.</p>
        )}
      </section>

      <section className="settings__block">
        <h2>Verrouillage</h2>
        <p className="quiet">
          {biometrics
            ? 'L’appareil demande votre empreinte ou votre NIP après quelques minutes d’inactivité.'
            : 'Cet appareil n’a pas de vérification intégrée : le verrouillage se limite à une confirmation.'}
        </p>
      </section>

      <section className="settings__block">
        <h2>Appareils</h2>
        <ul className="devices">
          {devices.map((device) => (
            <li key={device.id}>
              <p>
                {device.label ?? device.userAgent?.slice(0, 40) ?? 'Appareil'}
                {device.current ? ' · celui-ci' : ''}
              </p>
              <p className="meta">
                {device.revokedAt ? 'révoqué' : device.lastUsedAt ? `vu ${shortTime(device.lastUsedAt)}` : '—'}
              </p>
              {!device.current && !device.revokedAt && (
                <button
                  type="button"
                  className="ghost ghost--danger"
                  onClick={async () => {
                    await apiJson(`/auth/devices/${device.id}/revoke`, { method: 'POST' });
                    setDevices((current) =>
                      current.map((row) => (row.id === device.id ? { ...row, revokedAt: new Date().toISOString() } : row)),
                    );
                  }}
                >
                  Révoquer
                </button>
              )}
            </li>
          ))}
        </ul>
        <p className="quiet">
          Un appareil révoqué efface tout son contenu local à son prochain contact — y compris le travail non
          envoyé. Les heures se ressaisissent ; la liste des clients, non.
        </p>
      </section>

      <section className="settings__block">
        <BigButton
          tone="quiet"
          onClick={async () => {
            const result = await clearLocalData();
            setMessage(
              result.cleared
                ? 'Données locales effacées.'
                : `Impossible : ${result.pending} élément(s) ne sont pas encore envoyés.`,
            );
          }}
        >
          Effacer les données locales
        </BigButton>
        {message && <p className="quiet">{message}</p>}
      </section>
    </div>
  );
}
